// @ts-check
import Enumerable from "linq";

const lowercaseCharset = "abcdefghijklmnopqrstuvwxyz";
const uppercaseCharset = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const numericCharset = "0123456789";
const specialCharset = "!\"#$%&()*+,-./:;<=>?@[\\]^_";
const allCharset = lowercaseCharset + uppercaseCharset + numericCharset + specialCharset;

class Helper {
    StringTruncateToDisplayUserNameOrContactName(str) {
        return this.StringTruncateToDisplay(str, 50);
    }

    StringTruncateToDisplayContactPost(str) {
        return this.StringTruncateToDisplay(str, 100);
    }

    /**
     * @param {string} str
     * @returns {string | null}
     */
    StringTruncateToDisplayUserDlTitle(str) {
        return this.StringTruncateToDisplay(str, 200);
    }

    /**
     * @param {string} str
     * @param {number} wordMaxLength
     * @returns {string | null}
     */
    StringTruncateToDisplay(str, wordMaxLength) {
        if (wordMaxLength == null || wordMaxLength < 1)
            wordMaxLength = 40;
        if (str == null)
            return null;
        return (
            str.length > wordMaxLength
                ? (str.substring(0, wordMaxLength - 1 - 3) + "...")
                : str
        );
    }

    /**
     * @template T
     * @param {T[]} arr1
     * @param {T[]} arr2
     * @param {(a: T)=>unknown} funcSelector
     * @returns {boolean}
     */
    ArrayCompare(arr1, arr2, funcSelector) {
        if (arr1 == null || arr2 == null || arr1?.length != arr2?.length)
            return false;
        if (funcSelector == null)
            funcSelector = it => it;
        let arr1Sorted = Enumerable.from(arr1).orderBy(funcSelector).toArray();
        let arr2Sorted = Enumerable.from(arr2).orderBy(funcSelector).toArray();
        return arr1Sorted.every((it, ind) => funcSelector(it) == funcSelector(arr2Sorted[ind]));
    }

    /**
     * @template T
     * @param {T[]} arraySource
     * @param {number} chunkSize
     */
    ArrayChunk(arraySource, chunkSize) {
        let chunks = [];
        for (let i = 0; i < arraySource.length; i += chunkSize) {
            const chunk = arraySource.slice(i, i + chunkSize);
            chunks.push(chunk);
        }
        return chunks;
    }

    /**
     * @param {string} jsonText
     */
    JsonParse(jsonText) {
        try {
            let retJson = JSON.parse(jsonText, (key, value, context) => {
                if (context?.source && key?.length > 0) {
                    let tmp = this.#JsonParsePreparePropertyValue(value);
                    if (tmp.isConverted) {
                        return tmp.result;
                    }
                }
                return value;
            });
            return retJson;
        } catch (e) {
            console.log(`json parse error. source text: ${jsonText}`);
        }
        return null;
    }

    /**
     * Create deep clone of object
     * @template T
     * @param {T} obj 
     * @returns {T}
     */
    CloneObject(obj) {
        if (obj == null) {
            // @ts-ignore
            return null;
        }

        if (obj instanceof Date) {
            // @ts-ignore
            return new Date(obj.getTime());
        }

        if (obj instanceof Array) {
            // @ts-ignore
            return obj.map(i => this.CloneObject(i));
        }

        if (obj instanceof Object) {
            let newObj = {};
            // @ts-ignore
            Object.keys(obj).forEach(k => newObj[k] = this.CloneObject(obj[k]))
            // @ts-ignore
            return newObj;
        }

        let tmp = this.#JsonParsePreparePropertyValue(obj);
        if (tmp.isConverted) {
            // @ts-ignore
            return tmp.result;
        }

        //try {
        //    return structuredClone(obj); // передаёт по ссылке строку
        //}
        //catch (e) {
        //}

        return JSON.parse(JSON.stringify(obj));
    }

    /**
     * @param {any} obj
     */
    #JsonParsePreparePropertyValue(obj) {
        // преобразование даты из строки в объект при получении данных с backend
        if ((typeof obj == "string") && obj.includes("T") && isNaN(Date.parse(obj)) == false) {
            let dt = new Date(obj);
            let dateString = dt.getFullYear() + "-" + (dt.getMonth() + 1).toString().padStart(2, "0") + "-" + dt.getDate().toString().padStart(2, "0");
            if (dateString == obj.split("T")[0]) {
                return { isConverted: true, result: dt };
            }
        }
        return { isConverted: false };
    }

    /**
     * @param {number} length 
     * @returns {string}
     */
    GeneratePassword(length = 12) {
        if (length < 4)
            return "";

        // Генерация рандомной строки (массив символов)
        let allCharsetLength = allCharset.length;
        /** @type {string[]} */
        let password = [];

        for (var i = 0; i < length; i++) {
            let index = this.GetRandomIntInclusive(0, allCharsetLength - 1);
            let ch = allCharset[index];
            password.push(ch);
        }

        // Дополнительная обработка, чтобы в пароль входил
        // хотя бы один символ из каждой группы
        // (нижний / верхний регистры, цифры и спецсимволы)
        /** @type {number[]} */
        var lockedIndexies = [];
        this.CheckPassword(password, lowercaseCharset, lockedIndexies);
        this.CheckPassword(password, uppercaseCharset, lockedIndexies);
        this.CheckPassword(password, numericCharset, lockedIndexies);
        this.CheckPassword(password, specialCharset, lockedIndexies);

        return password.join("");
    }

    /**
     * @param {string[]} password
     * @param {string} charset
     * @param {number[]} locked
     * @returns {void}
     */
    CheckPassword(password, charset, locked) {
        if (password.length - locked.length < 0)
            return;

        for (var i = 0; i < password.length; i++) {
            if (charset.includes(password[i])) {
                locked.push(i);
                return;
            }
        }

        var ch = charset[this.GetRandomIntInclusive(0, charset.length - 1)];
        var allowIndexies = Array
            .from({ length: password.length }, (v, k) => k)
            .filter(i => locked.includes(i) == false);
        var index = allowIndexies[this.GetRandomIntInclusive(0, allowIndexies.length - 1)];
        password[index] = ch;
        locked.push(index);
    }

    /**
     * @param {number} min
     * @param {number} max
     * @returns {number}
     */
    GetRandomIntInclusive(min, max) {
        var randomBuffer = new Uint32Array(1);

        window.crypto.getRandomValues(randomBuffer);

        let randomNumber = randomBuffer[0] / (0xffffffff + 1);

        min = Math.ceil(min);
        max = Math.floor(max);
        return Math.floor(randomNumber * (max - min + 1)) + min;
    }

    /**
     * @param {number} ms
     * @returns {Promise<unknown>}
     */
    Delay(ms) {
        return new Promise(r => setTimeout(r, ms));
    }

    /**
     * @param {string} value 
     * @param {string} prefix 
     * @returns {string}
     */
    RemovePrefix(value, prefix) {
        return `${value}`.startsWith(`${prefix}`)
            ? `${value}`.slice(`${prefix}`.length)
            : value;
    }

    /**
     * @template T
     * @param {T[]} array
     * @param {T} item
     * @returns {boolean}
     */
    RemoveItem(array, item) {
        var index = array.indexOf(item);
        if (index >= 0) {
            array.splice(index, 1);
            return true;
        }
        return false;
    }

    /**
     * @param {string} text
     * @returns {string}
     */
    GetValidFileName(text) {
        return text.replace(/[\/|\\:*?"<>]/g, " ");
    }

    /**
     * 
     * @param {number} num 
     * @returns {string}
     * @private
     */
    padDateNum(num) {
        return (num < 10 ? '0' : '') + num;
    }

    /**
     * @param {Date | null} date 
     * @returns {string | null}
     */
    ToUserFrendlyDateOnlyString(date) {
        if (date == null)
            return null;

        let day = date.getDate();
        let month = date.getMonth() + 1;

        return this.padDateNum(day) +
            '.' + this.padDateNum(month) +
            '.' + date.getFullYear();
    }
    /**
     * @param {Date | null} date 
     * @returns {string | null}
     */
    ToUserFrendlyTimeOnlyString(date) {
        if (date == null)
            return null;

        let hours = date.getHours();
        let minutes = date.getMinutes();
        let seconds = date.getSeconds();

        return this.padDateNum(hours) +
            ':' + this.padDateNum(minutes) +
            ':' + this.padDateNum(seconds);
    }

    /**
     * @param {Date | null} date 
     * @returns {string | null}
     */
    ToUserFrendlyTimeAndDateString(date) {
        if (date == null)
            return null;

        return this.ToUserFrendlyTimeOnlyString(date) + ' ' + this.ToUserFrendlyDateOnlyString(date);
    }

    /**
     * @param {Date | null} date 
     * @returns {string | null}
     */
    ToUserFrendlyDateAndTimeString(date) {
        if (date == null)
            return null;

        return this.ToUserFrendlyDateOnlyString(date) + ' ' + this.ToUserFrendlyTimeOnlyString(date);
    }

    /**
     * @param {string | Date | null} dateOrIsoString
     */
    ParseOrGetDate(dateOrIsoString) {
        if (dateOrIsoString == null)
            return null;
        return (dateOrIsoString instanceof Date) ? dateOrIsoString : new Date(dateOrIsoString);
    }

    /**
     * @param {string | Date | null} dateOrIsoString
     */
    DateFormatDDMMYYYY(dateOrIsoString) {
        let date = this.ParseOrGetDate(dateOrIsoString);
        return date == null ? null : `${date.getDate().toString().padStart(2, "0")}.${(date.getMonth() + 1).toString().padStart(2, "0")}.${date.getFullYear()}`;
    }

    /**
     * @param {string | Date | null} dateOrIsoString
     */
    DateTimeFormatHHmmss(dateOrIsoString) {
        let date = this.ParseOrGetDate(dateOrIsoString);
        return date == null ? null : `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}:${date.getSeconds().toString().padStart(2, "0")}`;
    }

    /**
     * @param {string | Date | null} dateOrIsoString
     */
    DateTimeFormatDDMMYYYYHHmmss(dateOrIsoString) {
        let date = this.ParseOrGetDate(dateOrIsoString);
        return date == null ? null : `${this.DateFormatDDMMYYYY(date)} ${this.DateTimeFormatHHmmss(date)}`;
    }

    /**
     * @param {Date | null} date1
     * @param {Date | null} date2
     */
    DatesDiffDays(date1, date2) {
        if (!date1 ||
            !date2) {
            return null;
        }

        let diffMilliseconds = Math.abs(date2.getTime() - date1.getTime());
        let diffDays = diffMilliseconds / (1000 * 60 * 60 * 24);
        return diffDays;
    }

    /**
     * @param {number} min
     * @param {number} max
     */
    RandomInteger(min, max) {
        let rand = min + Math.random() * (max + 1 - min);
        return Math.floor(rand);
    }

    colors = [
        '#0088fe', '#00c49f', '#8884d8', '#82ca9d', '#83a6ed', '#a4de6c', '#8dd1e1', '#d0ed57', '#8889dd', '#8dc77b', '#9597e4', '#a5d297', '#ffbb28'
    ];
    /**
     * @param {number} ind
     */
    GetGraphColor(ind) {
        let barColor = this.colors[ind];
        if (barColor == null) {
            let isEven = (ind / 2) == Math.trunc(ind / 2);
            let r = this.RandomInteger(0, 150);
            let g = isEven ? this.RandomInteger(50, 100) : this.RandomInteger(100, 200);
            let b = isEven ? this.RandomInteger(100, 200) : this.RandomInteger(50, 100);
            barColor = `rgb(${r},${g},${b})`;
        }
        return barColor;
    }

    /**
     * @template {string | number | boolean | Object | Array<any>} T
     * @param {T | (() => T)} funOrVal
     * @returns {T}
     */
    CallFunctionOrValue(funOrVal) {
        return typeof funOrVal === "function" ? funOrVal() : funOrVal;
    }

    /**
     * @param {string} path
     * @param {any} params
     */
    PushOrUpdateHistory(path, params) {
        const url = new URL(window.location.toString());
        Object.keys(params).forEach(key => {
            const value = params[key];
            switch (typeof value) {
                case 'string':
                case 'bigint':
                case 'number':
                    url.searchParams.set(key, value.toString());
                    break;
                default:
                    url.searchParams.delete(key);
                    break;
            }
        });
        const currentPath = this.RemovePrefix(location.pathname, window.SmrLocation);

        if (currentPath == path) {
            window.history.replaceState(null, "", url.toString());
        }
        else {
            window.history.pushState(null, "", url.toString());
        }
    }

    IsBrowserEdge() {
        return navigator.userAgent.includes("Edg");
    }

}

export default new Helper;